import { graphql } from "gatsby";
import React from "react";

import Layout from "../../components/layout";
import IngoProgramPageComponent from "../../components/program/ingoProgramPageComponent";
import JourneysProgramPageComponent from "../../components/program/journeysProgramPageComponent";
import ProgramPageComponent from "../../components/program/programPageComponent";
import Seo from "../../components/seo";
import { ThemeContext, useTheme } from "../../context/ThemeContext";
import { dateFormat } from "../../utils/dateFormat";
import { formatCurrencyAmount } from "../../utils/formatContent";

const ProgramTemplate = ({ data, location }) => {
  const { currentSiteMeta } = useTheme();

  const program = data.program;
  const programDetails = program.acfProgramsMeta;
  const programName = program && program.title ? program.title : "";
  const programId = program?.databaseId ?? "";
  const programCode = programDetails?.programCode ?? programId;

  const currencyToUse = currentSiteMeta?.currency
    ? currentSiteMeta.currency.toLowerCase()
    : process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const programIdentifier = programCode ?? programId;
  const programSuffix = /[a-zA-Z]/.test(programIdentifier)
    ? ["program_code", programIdentifier].join("=")
    : ["program_id", programIdentifier].join("=");

  let applyNow = programId
    ? "/application-form/?" + programSuffix
    : "/application-form/";
  let enquireNow = programId
    ? `/enquiry-form/?type=specific_enquiry&program=${programId}&program_title=${programName}`
    : "/enquiry-form/";

  const journeysProgram = programDetails?.programJourneys;

  const ingoProgram = programDetails?.programIngo;
  // console.debug(ingoProgram);
  const ingoName = programDetails?.ingoBioName ?? "";

  if (journeysProgram) {
    applyNow = programId
      ? `/application-form/?${programSuffix}&journey_program`
      : `/application-form/?journey_program`;
    enquireNow = programId
      ? `/enquiry-form/?type=specific_enquiry&program=${programId}&journey_program`
      : "/enquiry-form/?journey_program";
  } else if (ingoProgram) {
    applyNow = programId
      ? `/application-form/?${programSuffix}&flex_program&flex_name=${ingoName}`
      : `/application-form/?flex_program&flex_name=${ingoName}`;
    enquireNow = programId
      ? `/enquiry-form/?type=specific_enquiry&program=${programId}&program_title=${programName}&flex_program`
      : "/enquiry-form/?flex_program";
  }

  const currentMaxPriceFilter = programDetails?.minimumPriceFilter?.find(
    (filter) =>
      filter?.maxPriceFilterCurrencyIsoCode?.toLowerCase() === currencyToUse
  );
  const minPriceFilterAmount = ingoProgram
    ? currentMaxPriceFilter?.maxPriceFilterDailyAmount
    : currentMaxPriceFilter?.maxPriceFilterAmount ?? "";
  const programPrice =
    [
      formatCurrencyAmount({
        currentSiteMeta,
        amount: minPriceFilterAmount
      }),
      ingoProgram ? " per day" : ""
    ].join("") ?? "";

  const programTypes = programDetails?.programTypes?.flatMap(
    ({ slug }) => slug
  );

  const displayProgramPrice = minPriceFilterAmount?.length > 0;
  const showPricingStickyWidget = false;

  const { stickyBookDescMobile } =
    data?.genericDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? {};

  const mobileStickyBookDescMobile = stickyBookDescMobile ?? "";

  const currentDate = new Date();
  const offerDate = dateFormat(
    {
      day: "numeric",
      month: "long"
    },
    new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
      23,
      59,
      59
    )
  );

  const programPromotion = mobileStickyBookDescMobile?.replace(
    "OFFER_DATE",
    offerDate
  );

  return (
    <ThemeContext.Consumer>
      {(theme) => (
        <Layout
          pageTitle={program.title}
          containerClass={"w-full mx-auto relative"}
          enquireNow={enquireNow}
          applyNow={applyNow}
          brandingOpts={{ ...theme }}
          programPrice={programPrice}
          programType={programTypes}
          showProgramPrice={displayProgramPrice}
          showPricingStickyWidget={showPricingStickyWidget}
          showPromotion={false}
          programPromotion={programPromotion}
          updateOnScroll={false}
          showGrantsBanner={true}
          additionalFooterOpts={{
            additionalFooterContainerClass: ingoProgram ? "xxxs:pb-64 " : ""
          }}
        >
          {journeysProgram ? (
            <JourneysProgramPageComponent
              data={data}
              location={location}
              theme={theme}
            />
          ) : ingoProgram ? (
            <IngoProgramPageComponent
              data={data}
              location={location}
              theme={theme}
            />
          ) : (
            <ProgramPageComponent
              data={data}
              location={location}
              theme={theme}
            />
          )}
        </Layout>
      )}
    </ThemeContext.Consumer>
  );
};

export const Head = ({ data }) => {
  const program = data?.program ?? {};
  const programDetails = program?.acfProgramsMeta;
  const location = program?.geographical?.nodes?.flatMap((item) => item.name);
  const programFeaturedImage =
    programDetails?.backgroundImage ??
    "https://cdn.gvi.co.uk/wp-content/uploads/sites/10/2018/03/6-1.jpg";

  const duration =
    programDetails?.durationDisplay
      ?.replaceAll(/\s|weeks|week/gi, "")
      ?.split("-")
      ?.map((v) => parseInt(v)) ?? "";

  const durationsListArr =
    duration?.length > 1
      ? Array.from(
          { length: duration[1] - duration[0] + 1 },
          (_, i) => i + duration[0]
        )
      : duration ?? "";

  const formatDurationList = (durationsListArr || [])?.map((item) => ({
    "@type": "CourseInstance",
    courseMode: "Onsite",
    location: location?.join(", ") || "",
    courseWorkload: `P${item}W`
  }));

  const courseDetails = {
    description:
      program?.seoLegacy?.description ?? program?.seo?.description ?? "",
    name: program?.seoLegacy?.title ?? program?.seo?.title ?? program.title,
    hasCourseInstance: [
      ...formatDurationList,
      {
        "@type": "CourseInstance",
        courseMode: "Online",
        courseWorkload: "P2D"
      }
    ],
    offers: [
      {
        "@type": "Offer",
        category: "Paid"
      }
    ]
  };

  return (
    <Seo
      title={program?.seoLegacy?.title ?? program?.seo?.title ?? program.title}
      description={
        program?.seoLegacy?.description ?? program?.seo?.description ?? ""
      }
      image={programFeaturedImage}
      translations={program?.translations}
      lang={program?.locale}
      courseDetails={courseDetails}
      meta={[
        {
          name: "robots",
          content: program?.seoLegacy?.robots ?? program?.seo?.robots
        }
      ]}
    />
  );
};

export const programQuery = graphql`
  query ($slug: String!) {
    program: wpProgram(slug: { eq: $slug }) {
      ...programFullDataFragment
    }
    genericDetails: wp {
      itinerary: acfOptionsItinerary {
        ...WpItineraryFragment
      }
      countryExploration: gviCountryExplorationSettings {
        ...WpCountryExplorationFragment
      }
      globalProgramConfig: gviConfigSettings {
        acfGenericGlobalConfig {
          meetTheTeamGetAcquaintedText
          meetTheTeamDesc
          pgmActivitySectionDesc
          pgmActivitySectionTitle
          pgmHighlightSectionDesc
          pgmHighlightSectionTitle
          teenBrochureTitle
          teenBrochureDownloadLink
          teenBrochureDescription
          bookWithConfidenceText
          bookWithConfidenceTitle
          trustpilotRating
          programImpactTitle
          programImpactList {
            impact
          }
          ...AlumniGalleryFragment
          ...ExclusiveOffersFragment
          ...StickyWidgetFragment
          ...TeenStickyFragment

          programDatesAndPriceList {
            title
            description
          }
          exclusiveOffersAmount {
            exclOffersCurrencyIsoCode
            exclOffersAmount
          }
        }
      }

      ingoPrograms: acfOptionsIngoProgramsConfig {
        ingoPrograms {
          ingoGlobalSupportFeeDesc
          journeysGlobalSupportFeeDesc
          journeysGlobalFundsSpent
          sidebarApplicationWidget {
            ingoExclusiveOfferTitle
            ingoExclusiveOfferDesc
            exclusiveOfferImage {
              ...smallGalleryData
            }
          }
          sidebarBookWithConfidenceWidget {
            ingoBookWithConfidenceTitle
            bookWithConfidenceList {
              ingoListItem
            }
            stickyBottomWidget {
              ingoStickyTitle
              ingoStickyDesc
              ingoStickyBookNowText
              ingoStickyImage {
                ...smallGalleryData
              }
            }
          }
          ingoBrochures {
            brochureFile {
              slug
              uri
              sourceUrl
              mediaItemUrl
            }
            brochureGeographical {
              uri
              slug
              name
              id
              description
            }
          }
        }
      }
    }
  }
`;
export default ProgramTemplate;
